import { useState } from 'react';
import { DataTable } from '../ui/DataTable';
import { Button } from '../ui/Button';
import { DropdownMenu } from '../ui/DropdownMenu';
import { 
  Check, 
  Eye, 
  Edit2, 
  Trash2, 
  CheckSquare, 
  MoreVertical, 
  FileText, 
  Download,
  Upload,
  Paperclip,
  AlertTriangle
} from 'lucide-react';
import { JOURNAL_TYPES } from '../../config/accounting';
import type { JournalEntry } from '../../types/accounting';
import { useCurrency } from '../../hooks/useCurrency';

interface UnifiedJournalListProps {
  entries: JournalEntry[];           // Entrées déjà paginées et filtrées (inclut agent si applicable)
  agentEntries: JournalEntry[];      // Liste complète des entrées agent (pour la sélection/validation en lot)
  onView: (entry: JournalEntry) => void;
  onEdit: (entry: JournalEntry) => void;
  onDelete: (entry: JournalEntry) => void;
  onValidate?: (entry: JournalEntry) => void;
  onValidateAll?: (entries: JournalEntry[]) => void;
  onExport: (format: 'pdf' | 'excel' | 'csv') => void;
  onUploadAttachment?: (entry: JournalEntry, file: File) => void;
  onDownloadAttachment?: (entry: JournalEntry, attachmentId: string) => void;
  loading?: boolean;
  page: number;
  totalPages: number;
  totalItems?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export function UnifiedJournalList({
  entries,           // Entrées déjà paginées - NE PAS re-combiner
  agentEntries,      // Utilisé seulement pour la sélection en lot
  onView,
  onEdit,
  onDelete,
  onValidate,
  onValidateAll,
  onExport,
  onUploadAttachment,
  onDownloadAttachment,
  loading,
  page,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange
}: UnifiedJournalListProps) {
  const [selectedEntries, setSelectedEntries] = useState<JournalEntry[]>([]);
  
  // Les entrées sont déjà filtrées et paginées par le parent (JournalsPage)
  // Ne PAS re-combiner pour éviter les doublons
  const paginatedEntries = entries;
  
  const isAllSelected = agentEntries.length > 0 && selectedEntries.length === agentEntries.length;
  const { formatConverted } = useCurrency();
  
  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries([...agentEntries]);
    }
  };

  const toggleSelectEntry = (entry: JournalEntry) => {
    if (selectedEntries.find(e => e.id === entry.id)) {
      setSelectedEntries(selectedEntries.filter(e => e.id !== entry.id));
    } else {
      setSelectedEntries([...selectedEntries, entry]);
    }
  };

  const getRowClassName = (entry: JournalEntry) => {
    if (entry.source === 'agent' && entry.validationStatus === 'pending') {
      return 'bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/20 dark:hover:bg-orange-900/30';
    }
    
    if (entry.status === 'posted') {
      return 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-800/70';
    }
    
    if (entry.status === 'approved') {
      return 'bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30';
    }

    if (entry.status === 'pending') {
      return 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30';
    }
    
    return '';
  };

  const getStatusBadge = (status: JournalEntry['status']) => {
    const statusConfig = {
      draft: { label: 'Brouillon', className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
      pending: { label: 'En attente', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' },
      approved: { label: 'Approuvé', className: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' },
      posted: { label: 'Comptabilisé', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const columns = [
    {
      header: 'Date',
      accessor: (entry: JournalEntry) => new Date(entry.date).toLocaleDateString('fr-FR')
    },
    {
      header: 'N° Pièce',
      accessor: (entry: JournalEntry) => entry.reference
    },
    {
      header: 'Journal',
      accessor: (entry: JournalEntry) => {
        // Mapping between journalType and JOURNAL_TYPES constant keys
        const journalTypeMapping: { [key: string]: keyof typeof JOURNAL_TYPES } = {
          'sales': 'SALES',
          'purchases': 'PURCHASE',
          'bank': 'BANK',
          'cash': 'CASH',
          'general': 'GENERAL'
        };
        
        const mappedType = journalTypeMapping[entry.journalType];
        return mappedType ? JOURNAL_TYPES[mappedType].label : entry.journalType;
      }
    },
    {
      header: 'Comptes',
      accessor: (entry: JournalEntry) => {
        // Protection contre lines undefined ou non-array
        const safeLines = Array.isArray(entry.lines) ? entry.lines : [];
        return (
          <div className="text-sm">
            {safeLines.map((line, index) => (
              <div key={line.id || `line-${index}`} className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  {line.accountCode || '-'}
                </span>
                <span className="text-gray-600 dark:text-gray-400 truncate max-w-32">
                  {line.accountName || '-'}
                </span>
              </div>
            ))}
            {safeLines.length === 0 && (
              <span className="text-gray-400 italic text-xs">Aucune ligne</span>
            )}
          </div>
        );
      }
    },
    {
      header: 'Libellé',
      accessor: (entry: JournalEntry) => (
        <div className="flex items-center">
          {entry.source === 'agent' && (
            <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800 mr-2">
              ADHA
            </span>
          )}
          <span className="truncate">{entry.description}</span>
        </div>
      )
    },
    {
      header: 'Débit',
      accessor: (entry: JournalEntry) => formatConverted(entry.totalDebit),
      className: 'text-right'
    },
    {
      header: 'Crédit',
      accessor: (entry: JournalEntry) => formatConverted(entry.totalCredit),
      className: 'text-right'
    },
    {
      header: 'Statut',
      accessor: (entry: JournalEntry) => getStatusBadge(entry.status)
    },
    {
      header: 'Pièces',
      accessor: (entry: JournalEntry) => (
        <div className="flex items-center gap-2">
          {entry.attachments && entry.attachments.length > 0 ? (
            <div className="flex items-center gap-1">
              <Paperclip className="h-4 w-4 text-green-600" />
              <span className="text-xs text-green-600">{entry.attachments.length}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <span className="text-xs text-orange-600">0</span>
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Actions',
      accessor: (entry: JournalEntry) => {
        const isPosted = entry.status === 'posted';
        const isAgentPending = entry.source === 'agent' && entry.validationStatus === 'pending';
        
        const menuItems = [
          {
            label: 'Voir les détails',
            icon: Eye,
            onClick: () => onView(entry)
          },
          ...(entry.attachments && entry.attachments.length > 0 ? [{
            label: 'Télécharger pièces',
            icon: Download,
            onClick: () => entry.attachments?.forEach(att => 
              onDownloadAttachment && onDownloadAttachment(entry, att.id)
            )
          }] : []),
          ...(!isPosted && onUploadAttachment ? [{
            label: 'Ajouter pièce',
            icon: Upload,
            onClick: () => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.pdf,.jpg,.jpeg,.png';
              input.onchange = (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) onUploadAttachment(entry, file);
              };
              input.click();
            }
          }] : []),
          ...(!isPosted ? [{
            label: 'Modifier',
            icon: Edit2,
            onClick: () => onEdit(entry)
          }] : []),
          ...(isAgentPending && onValidate ? [{
            label: 'Valider',
            icon: Check,
            onClick: () => onValidate(entry)
          }] : []),
          ...(!isPosted ? [{
            label: 'Supprimer',
            icon: Trash2,
            onClick: () => onDelete(entry),
            variant: 'danger' as const
          }] : [])
        ];

        return (
          <DropdownMenu
            trigger={
              <Button
                variant="ghost"
                size="sm"
                icon={MoreVertical}
                className="h-8 w-8 p-0"
              />
            }
            items={menuItems}
          />
        );
      }
    }
  ];
  // Si des entrées d'agent sont présentes dans la page, ajouter une colonne de sélection
  if (agentEntries.length > 0) {
    columns.unshift({
      header: 'Sélection',
      accessor: (entry: JournalEntry) => (
        entry.source === 'agent' && entry.validationStatus === 'pending' ? (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={Boolean(selectedEntries.find(e => e.id === entry.id))}
              onChange={() => toggleSelectEntry(entry)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
          </div>
        ) : null
      ),
      className: 'w-10'
    } as any); // Type assertion to any to avoid TypeScript errors
  }

  // Compter les entrées ADHA dans la liste paginée actuelle
  const agentEntriesInPage = paginatedEntries.filter(e => e.source === 'agent' && e.validationStatus === 'pending').length;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          {/* Bouton de validation en lot si des entrées sont sélectionnées */}
          {selectedEntries.length > 0 && onValidateAll && (
            <Button
              variant="primary"
              size="sm"
              icon={CheckSquare}
              onClick={() => onValidateAll(selectedEntries)}
            >
              Valider la sélection ({selectedEntries.length})
            </Button>
          )}
          {/* Info sur les entrées ADHA à valider */}
          {agentEntriesInPage > 0 && (
            <span className="text-sm text-orange-600 dark:text-orange-400">
              {agentEntriesInPage} écriture{agentEntriesInPage > 1 ? 's' : ''} ADHA en attente de validation
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">
            {paginatedEntries.length} écriture{paginatedEntries.length > 1 ? 's' : ''} affichée{paginatedEntries.length > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Sélection en lot des entrées ADHA si il y en a */}
      {agentEntriesInPage > 0 && (
        <div className="flex items-center mb-2 bg-orange-50 dark:bg-orange-900/20 p-2 rounded">
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={toggleSelectAll}
            className="mr-2 rounded border-gray-300 text-primary focus:ring-primary"
            id="select-all"
          />
          <label htmlFor="select-all" className="text-sm text-orange-800 dark:text-orange-200">
            Sélectionner toutes les écritures ADHA en attente ({agentEntries.filter(e => e.validationStatus === 'pending').length})
          </label>
        </div>
      )}

      <DataTable
        columns={columns}
        data={paginatedEntries}
        loading={loading}
        emptyMessage="Aucune écriture trouvée. Utilisez les filtres pour affiner votre recherche ou créez une nouvelle écriture."
        emptyTitle="Aucune écriture comptable"
        emptyAction={{
          label: "Créer une écriture",
          onClick: () => console.log("Créer une nouvelle écriture"),
          icon: 'plus'
        }}
        rowClassName={getRowClassName}
        showToolbar={true}
        showPagination={true}
        enableResize={true}
        enableSort={true}
        enableFilters={true}
        enableColumnToggle={true}
        enableGlobalFilter={true}
        storageKey="unified-journal-list"
        maxHeight="calc(100vh - 400px)"
        density="normal"
        // Pagination externe
        page={page}
        totalPages={Math.max(1, totalPages)}
        totalItems={totalItems ?? (paginatedEntries.length * totalPages)}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        toolbarActions={
          <DropdownMenu
            trigger={
              <Button variant="secondary" size="sm" icon={Download}>
                <span className="hidden sm:inline">Exporter</span>
              </Button>
            }
            items={[
              {
                label: 'Export PDF',
                icon: FileText,
                onClick: () => onExport('pdf')
              },
              {
                label: 'Export Excel',
                icon: FileText,
                onClick: () => onExport('excel')
              },
              {
                label: 'Export CSV',
                icon: Download,
                onClick: () => onExport('csv')
              }
            ]}
          />
        }
      />
    </div>
  );
}
